<script setup lang="ts">
import { useAllianceStore } from '@/stores/allianceStore';
import { useGameStore } from '@/stores/gameStore';
import { computed } from 'vue';
import { useRouter } from 'vue-router';
import StandardCard from '@/components/Elements/Cards/StandardCard.vue';
import DeleteButton from '@/components/Elements/Buttons/DeleteButton.vue';
import Backend from '@/models/backend';
import ActionButton from '@/components/Elements/Buttons/ActionButton.vue';
import { logger } from '@/models/logger';
import { useI18n } from 'vue-i18n'

const { t } = useI18n()

// Store
const gameStore = useGameStore()
const allianceStore = useAllianceStore()
const router = useRouter()

// Computed

// Get Alliance data from store
const alliance = computed(() => {
    return allianceStore.getAlliance(gameStore.player!.allianceId)!
})

const isLeader = computed(() => {
  return alliance.value.leaderPlayerId === gameStore.player!.id
})

// Methods
// Disband Alliance
const disbandAlliance = () => {
  // Show confirmation modal
  gameStore.modal2Type = 'confirmationDelete'
  gameStore.modal2Data = {
    title: t('diplomacy.alliance.disbandAlliance'),
    message: t('diplomacy.alliance.disbandConfirmMessage', { name: alliance.value.name }),
    cancelText: t('common.actions.cancel'),
    actionText: t('diplomacy.alliance.disband'),
    action: async () => {
      // Make API call to backend to disband the alliance
      const backend = new Backend()
      backend.leaveAlliance().then(() => {
        // Show success message
        gameStore.setTinySuccessToastMessage(t('notifications.success.allianceDisbanded'))
        // Redirect to diplomacy page
        router.push('/diplomacy/alliances')
      }).catch((error) => {
        // Show error message
        gameStore.setTinyErrorToastMessage(t('diplomacy.alliance.failedToDisband'))
        logger.error(error)
      })
    }
  }
}

// Leave Alliance
const leaveAlliance = () => {
  // Show confirmation modal
  gameStore.modal2Type = 'confirmationDelete'
  gameStore.modal2Data = {
    title: t('diplomacy.alliance.leaveAlliance'),
    message: t('diplomacy.alliance.leaveConfirmMessage', { name: alliance.value.name }),
    cancelText: t('common.actions.cancel'),
    actionText: t('common.actions.leave'),
    action: async () => {
      // Make API call to backend to leave the alliance
      const backend = new Backend()
      backend.leaveAlliance().then(() => {
        // Show success message
        gameStore.setTinySuccessToastMessage(t('notifications.success.allianceLeft'))
        // Redirect to diplomacy page
        router.push('/diplomacy/alliances')
      })
    }
  }
}

// Edit Alliance Description
const editAllianceDescription = () => {
  if (!isLeader.value) return
  gameStore.modalType = 'editAllianceDescription'
  gameStore.modalData = {}
}

// Edit Alliance Image
const editAllianceImage = () => {
  if (!isLeader.value) return
  gameStore.modalType = 'editAllianceImage'
  gameStore.modalData = {}
}

</script>

<template>
  <StandardCard :title="t('diplomacy.alliance.titleWithName', { name: alliance.name })" bottom-padding="2">
    <div class="flex flex-row gap-2">
      <div class="relative flex-shrink-0">
        <img :src="alliance.allianceImage" :alt="alliance.name" class="w-24 h-24 rounded object-cover">
        <button 
          v-if="isLeader" 
          @click="editAllianceImage" 
          class="absolute bottom-0 right-0 bg-gray-800 bg-opacity-70 text-white p-1 rounded-bl hover:bg-opacity-90"
          :title="t('diplomacy.alliance.changeImage')"
        >
          <svg xmlns="http://www.w3.org/2000/svg" class="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" />
          </svg>
        </button>
      </div>
      <div class="flex flex-col flex-grow">
        <p 
          class="hover:text-blue-400 transition-colors cursor-text"
          v-if="isLeader"
          @click="editAllianceDescription"
          :title="t('diplomacy.alliance.editDescription')"
        >
          {{ alliance.description || t('diplomacy.alliance.addDescription') }}
        </p>
        <p class="" v-else>{{ alliance.description }}</p>
      </div>
      <div class="flex flex-col justify-end gap-2">
        <DeleteButton @click="disbandAlliance" v-if="isLeader">{{ t('diplomacy.alliance.disband') }}</DeleteButton>
        <ActionButton @click="leaveAlliance" v-else>{{ t('common.actions.leave') }}</ActionButton>
      </div>
    </div>
  </StandardCard>
</template> 